import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from '@mui/material';
import {
    People as PeopleIcon,
    History as HistoryIcon,
    TrendingUp as TrendingUpIcon,
    School as SchoolIcon,
    Person as PersonIcon,
    AdminPanelSettings as AdminIcon,
    Psychology as PsychologyIcon,
    PersonOff as PersonOffIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../services/api';

const PlatformStatistics = () => {
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadStatistics();
    }, []);

    const loadStatistics = async () => {
        try {
            setLoading(true);
            const [response, accountActivityResponse] = await Promise.all([
                api.get('/admin/statistics'),
                api.get('/admin/accounts/activity-stats')
            ]);
            
            setStatistics({
                ...response.data,
                accountActivity: accountActivityResponse.data
            });
        } catch (err) {
            setError('Failed to load platform statistics');
            console.error('Error loading statistics:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    const { overview, topPerformers, activityTrend, accountActivity } = statistics;

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Platform Statistics
            </Typography>

            {/* Overview Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h4">
                                {overview.totalUsers}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Users
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <HistoryIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                            <Typography variant="h4">
                                {overview.totalHistories}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Histories
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <TrendingUpIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                            <Typography variant="h4">
                                {overview.recentActivity}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Recent Activity (7 days)
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <SchoolIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                            <Typography variant="h4">
                                {overview.usersByRole.students}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Students
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <PersonOffIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                            <Typography variant="h4">
                                {accountActivity?.deactivatedUsers || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Deactivated Users
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* User Distribution */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                User Distribution by Role
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <PersonIcon sx={{ fontSize: 30, color: 'primary.main' }} />
                                    <Typography variant="h6">{overview.usersByRole.students}</Typography>
                                    <Typography variant="body2" color="text.secondary">Students</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <PersonIcon sx={{ fontSize: 30, color: 'success.main' }} />
                                    <Typography variant="h6">{overview.usersByRole.doctors}</Typography>
                                    <Typography variant="body2" color="text.secondary">Doctors</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <PersonIcon sx={{ fontSize: 30, color: 'warning.main' }} />
                                    <Typography variant="h6">{overview.usersByRole.educators}</Typography>
                                    <Typography variant="body2" color="text.secondary">Educators</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <PsychologyIcon sx={{ fontSize: 30, color: 'info.main' }} />
                                    <Typography variant="h6">{overview.usersByRole.instructors}</Typography>
                                    <Typography variant="body2" color="text.secondary">Instructors</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <AdminIcon sx={{ fontSize: 30, color: 'error.main' }} />
                                    <Typography variant="h6">{overview.usersByRole.admins}</Typography>
                                    <Typography variant="body2" color="text.secondary">Admins</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Activity Trend */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Activity Trend (Last 30 Days)
                            </Typography>
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={activityTrend}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Line type="monotone" dataKey="count" stroke="#8884d8" />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Top Performers */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Top Performers
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Average Score</TableCell>
                                    <TableCell>Total Histories</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {topPerformers.map((performer, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{performer.name}</TableCell>
                                        <TableCell>{performer.email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={`${performer.averageScore}%`}
                                                color={performer.averageScore >= 80 ? 'success' : 'warning'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{performer.totalHistories}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default PlatformStatistics;
